import React from "react";
import styled from "styled-components";
import { TestResult } from "../types";
import { getStatusDisplayName, extractProjectStatusIds } from "../services/api";

interface ApiTestResult {
  testRunName: string;
  testSuiteName: string;
  testCaseName: string;
  testCaseMetaId: string;
  scriptTestRunResult?: {
    result_id: string;
  };
  [key: string]: any;
}

interface SidebarProps {
  testResults: TestResult[];
  selectedTestId: string | null;
  onSelectTest: (testId: string) => void;
  completedTests?: Set<string>;
  children?: React.ReactNode;
  apiTestResults?: ApiTestResult[];
  template?: any;
  projectStatusIds?: any; // extractProjectStatusIds(template) の結果
  groupInfo?: {
    testSuiteName: string;
    currentIndex: number;
    totalCount: number;
  } | null;
}

const SidebarContainer = styled.div`
  width: 300px;
  height: 100vh;
  background-color: #ffffff;
  border-right: 1px solid #e0e0e0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  flex-shrink: 0;
`;

const SidebarTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
`;

const SidebarFooter = styled.div`
  padding: 12px;
  border-top: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  flex-shrink: 0;
`;

const TestCount = styled.div`
  margin-top: 2px;
  font-size: 10px;
  color: #6c757d;
  display: flex;
  gap: 8px;
`;

const GroupInfoBadge = styled.div`
  margin-top: 4px;
  padding: 3px 8px;
  background-color: #e8f4fd;
  border: 1px solid #b8d9f0;
  border-radius: 4px;
  font-size: 11px;
  color: #1a73a7;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
`;

const GroupInfoSuiteName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const GroupInfoProgress = styled.span`
  flex-shrink: 0;
  font-weight: 600;
  color: #0d6efd;
`;

const TestStatus = styled.span<{ color: string }>`
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 600;
  color: white;
  background-color: ${(props) => props.color};
`;

const CountItem = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  font-weight: 500;
`;

const TestList = styled.div`
  padding: 4px 0;
  flex: 1;
  overflow-y: auto;
`;

const TestItem = styled.div<{
  isSelected: boolean;
  status: string;
  isCompleted: boolean;
}>`
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.1s;
  background-color: ${(props) => {
    if (props.isSelected) return "#e3f2fd";
    if (props.isCompleted) return "#f0f8ff";
    return "transparent";
  }};
  border-left: 4px solid ${(props) => {
    if (props.isCompleted) return "#2196f3";
    const mapped = mapStatus(props.status);
    if (mapped === "OK") return "#4caf50";
    if (mapped === "NG") return "#f44336";
    if (mapped === "Error") return "#e91e63";
    return "#9e9e9e";
  }};
  border-bottom: 1px solid #f5f5f5;
  position: relative;

  &:hover {
    background-color: ${(props) => (props.isSelected ? "#e3f2fd" : "#f8f9fa")};
  }
`;

const TestCaseName = styled.div<{ isCompleted: boolean }>`
  font-weight: 600;
  color: ${(props) => (props.isCompleted ? "#2196f3" : "#2c3e50")};
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TestHierarchy = styled.div`
  margin-bottom: 8px;
  font-size: 10px;
  line-height: 1.4;
`;

const HierarchyItem = styled.div<{ level: "run" | "suite" }>`
  color: ${(props) => {
    switch (props.level) {
      case "run":
        return "#28a745";
      case "suite":
        return "#007bff";
      default:
        return "#6c757d";
    }
  }};
  font-weight: 500;
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HierarchyLabel = styled.span`
  font-size: 8px;
  color: #6c757d;
  font-weight: 600;
  min-width: 60px;
  flex-shrink: 0;
`;

const ExecutionInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const ExecutionTime = styled.div`
  font-size: 10px;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AdditionalInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  font-size: 9px;
  color: #6c757d;
  border-top: 1px solid #f0f0f0;
  padding-top: 6px;
`;

const CompletedIcon = styled.span`
  font-size: 12px;
  color: #2196f3;
`;

const TestMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
`;

const TestDuration = styled.div`
  font-size: 9px;
  color: #6c757d;
`;

const StepsSummary = styled.div`
  font-size: 9px;
  color: #6c757d;
  display: flex;
  gap: 4px;
`;

const mapStatus = (status: string): "OK" | "NG" | "Error" => {
  switch (status) {
    case "success":
      return "OK";
    case "step_failed":
    case "result_failed":
      return "NG";
    case "error":
      return "Error";
    default:
      return "NG";
  }
};

const Sidebar: React.FC<SidebarProps> = ({
  testResults,
  selectedTestId,
  onSelectTest,
  completedTests = new Set(),
  children,
  apiTestResults = [],
  template,
  projectStatusIds,
  groupInfo,
}) => {
  const formatDuration = (duration: number): string => {
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const formatDateTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${month}/${day} ${hours}:${minutes}`;
  };

  const formatTraceId = (traceId: string): string => {
    // トレースIDを短縮して表示（最初の8文字）
    return traceId.length > 8 ? `${traceId.slice(0, 8)}...` : traceId;
  };

  const getTestHierarchy = (testId: string) => {
    const apiTestResult = apiTestResults.find(
      (result) => result.scriptTestRunResult?.result_id === testId
    );
    return {
      testRunName: apiTestResult?.testRunName || "",
      testSuiteName: apiTestResult?.testSuiteName || "",
      testCaseName: apiTestResult?.testCaseName || "",
      testCaseMetaId: apiTestResult?.testCaseMetaId || "",
    };
  };

  // 判定ロジック: ステータスIDからOK/NG/未実施/その他を返す
  const judgeStatus = (
    statusId: string | undefined
  ): "OK" | "NG" | "未実施" | "AI要確認" | "その他" => {
    if (!statusId || !projectStatusIds) return "未実施";
    if (statusId === projectStatusIds.OK || statusId === projectStatusIds.AI_OK)
      return "OK";
    if (
      statusId === projectStatusIds.NG ||
      statusId === projectStatusIds.AI_RESULT_NG ||
      statusId === projectStatusIds.AI_PROCEDURE_NG
    )
      return "NG";
    if (statusId === projectStatusIds.UNTESTED) return "未実施";
    if (
      statusId === projectStatusIds.AI_OK_NEED_REVIEW ||
      statusId === projectStatusIds.AI_RESULT_NG_NEED_REVIEW ||
      statusId === projectStatusIds.AI_PROCEDURE_NG_NEED_REVIEW
    )
      return "AI要確認";
    return "その他";
  };

  const getApiStatusInfo = (
    testId: string
  ): { judge: string; display: string; statusId: string | undefined } => {
    const apiResult = apiTestResults.find(
      (result: any) =>
        result.scriptTestRunResult &&
        result.scriptTestRunResult.result_id === testId
    );
    if (
      !apiResult ||
      !template ||
      !projectStatusIds ||
      !apiResult.scriptTestRunResult ||
      !("test_result_status_id" in apiResult.scriptTestRunResult)
    ) {
      return { judge: "未実施", display: "未実施", statusId: undefined };
    }
    const statusId = apiResult.scriptTestRunResult
      .test_result_status_id as string;
    const judge = judgeStatus(statusId);
    const display = getStatusDisplayName(template, statusId);
    return { judge, display, statusId };
  };

  const getStatusCounts = () => {
    const counts: Record<string, number> = {};
    testResults.forEach((test) => {
      const info = getApiStatusInfo(test.id);
      counts[info.judge] = (counts[info.judge] || 0) + 1;
    });
    counts["completed"] = completedTests.size;
    return counts;
  };

  const statusCounts = getStatusCounts();

  const getStepsSummary = (test: TestResult) => {
    const total = test.steps.length;
    const success = test.steps.filter(
      (step) => step.result === "success"
    ).length;
    const warning = test.steps.filter(
      (step) => step.result === "warning"
    ).length;
    const failed = test.steps.filter(
      (step) => step.result === "failure"
    ).length;
    const evidence = test.steps.filter((step) => step.evidence).length;
    return { total, success, warning, failed, evidence };
  };

  // 色分け関数
  const getStatusColor = (statusId: string | undefined): string => {
    if (!statusId || !projectStatusIds) return "#9e9e9e";
    if (statusId === projectStatusIds.OK || statusId === projectStatusIds.AI_OK)
      return "#4caf50"; // 緑
    if (
      statusId === projectStatusIds.NG ||
      statusId === projectStatusIds.AI_RESULT_NG ||
      statusId === projectStatusIds.AI_PROCEDURE_NG
    )
      return "#f44336"; // 赤
    if (
      statusId === projectStatusIds.AI_OK_NEED_REVIEW ||
      statusId === projectStatusIds.AI_RESULT_NG_NEED_REVIEW ||
      statusId === projectStatusIds.AI_PROCEDURE_NG_NEED_REVIEW
    ) {
      return "#ff9800"; // オレンジ
    }
    return "#9e9e9e";
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        <SidebarTitle>テスト結果 ({testResults.length})</SidebarTitle>
        {groupInfo && (
          <GroupInfoBadge>
            <GroupInfoSuiteName title={groupInfo.testSuiteName}>
              {groupInfo.testSuiteName}
            </GroupInfoSuiteName>
            <GroupInfoProgress>
              {groupInfo.currentIndex} / {groupInfo.totalCount}
            </GroupInfoProgress>
          </GroupInfoBadge>
        )}
        <TestCount>
          {Object.entries(statusCounts).map(([status, count]) => {
            // status名から代表的なstatusIdを取得
            let statusId = undefined;
            if (status === "OK") statusId = projectStatusIds?.OK;
            else if (status === "NG") statusId = projectStatusIds?.NG;
            else if (status === "未実施") statusId = projectStatusIds?.UNTESTED;
            else if (status === "AI要確認")
              statusId = projectStatusIds?.AI_OK_NEED_REVIEW;
            // その他はグレー
            return (
              <CountItem key={status} color={getStatusColor(statusId)}>
                {status}:{count}
              </CountItem>
            );
          })}
        </TestCount>
      </SidebarHeader>

      <TestList>
        {testResults.map((test) => {
          const stepsSummary = getStepsSummary(test);
          const isCompleted = completedTests.has(test.id);
          const hierarchy = getTestHierarchy(test.id);
          const info = getApiStatusInfo(test.id);

          return (
            <TestItem
              key={test.id}
              isSelected={selectedTestId === test.id}
              status={info.judge}
              isCompleted={isCompleted}
              onClick={() => onSelectTest(test.id)}
            >
              {/* 必須情報: テスト階層（ラベル付き） */}
              <TestHierarchy>
                <HierarchyItem level="run">
                  <HierarchyLabel>テストラン:</HierarchyLabel>
                  <svg width="8" height="8" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 9.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                    <path fillRule="evenodd" d="M8 0a8 8 0 100 16A8 8 0 008 0zM1.5 8a6.5 6.5 0 1113 0 6.5 6.5 0 01-13 0z"/>
                  </svg>
                  <span>{hierarchy.testRunName}</span>
                </HierarchyItem>
                <HierarchyItem level="suite">
                  <HierarchyLabel>テストスイート:</HierarchyLabel>
                  <svg width="8" height="8" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M1.75 1A1.75 1.75 0 000 2.75v10.5C0 14.216.784 15 1.75 15h12.5A1.75 1.75 0 0016 13.25v-8.5A1.75 1.75 0 0014.25 3H7.5a.25.25 0 01-.2-.1l-.9-1.2C6.07 1.26 5.55 1 5 1H1.75z"/>
                  </svg>
                  <span>{hierarchy.testSuiteName}</span>
                </HierarchyItem>
              </TestHierarchy>

              {/* 必須情報: テストケース名 */}
              <TestCaseName isCompleted={isCompleted}>
                {isCompleted && (
                  <CompletedIcon>
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/>
                    </svg>
                  </CompletedIcon>
                )}
                {test.metaId && `${test.metaId} `}{test.name}
              </TestCaseName>

              {/* 必須情報: 実行日時と結果 */}
              <ExecutionInfo>
                <ExecutionTime>
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M1.5 8a6.5 6.5 0 1113 0 6.5 6.5 0 01-13 0zM8 0a8 8 0 100 16A8 8 0 008 0zm.5 4.75a.75.75 0 00-1.5 0v3.5a.75.75 0 00.471.696l2.5 1a.75.75 0 00.557-1.392L8.5 7.742V4.75z"/>
                  </svg>
                  {formatDateTime(test.startTime)}
                </ExecutionTime>
                <TestStatus color={getStatusColor(info.statusId)}>
                  {info.display}
                </TestStatus>
              </ExecutionInfo>

              {/* 補助情報: 必要に応じて表示 */}
              <AdditionalInfo>
                <span>実行時間: {formatDuration(test.executionTime)}</span>
                <span>環境: {test.environment}</span>
                {stepsSummary.total > 0 && (
                  <span>
                    ステップ: {stepsSummary.total}
                    {stepsSummary.failed > 0 && ` (失敗: ${stepsSummary.failed})`}
                    {stepsSummary.warning > 0 && ` (要確認: ${stepsSummary.warning})`}
                  </span>
                )}
              </AdditionalInfo>
            </TestItem>
          );
        })}
      </TestList>

      {children && <SidebarFooter>{children}</SidebarFooter>}
    </SidebarContainer>
  );
};

export default Sidebar;
